import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTheme as useTenantTheme } from '../../context/ThemeContext';

// Shared monospace font for every terminal auth surface.
export const MONO = "'JetBrains Mono', monospace";

// Screen width (CSS px) below which terminal routes are blocked with a
// "Desktop required" card. Matches the tablet-landscape / small-laptop
// breakpoint: catches iPad landscape (1024) and all desktops; blocks
// phones and iPad portrait.
export const MOBILE_THRESHOLD = 1024;

// Derives the dark terminal palette from tenantConfig. Consumers that need
// palette values in their form internals (input styles, buttons, error
// banner, etc.) import this helper and call it with their own tenantConfig.
// Shell also uses it internally for the card chrome.
//
// NOTE on duplicate computation: paletteFrom is called independently by the
// shell and by each consumer. Acceptable since the result is a cheap pure
// derivation from tenantConfig. If palette derivation becomes expensive
// (e.g. contrast analysis, color-space conversions), lift to a React
// context provided by the shell instead of adding memoization here.
//
// NOTE on colors.secondary: tenantConfig.colors.secondary is used as
// terminal background because .background defaults to #ffffff (light theme
// surface) and is wrong for a terminal entry. This is a known semantic
// compromise — see "Kjente semantiske kompromisser" in
// docs/specs/terminal-standalone.md. When the tenant color schema is
// expanded with a dedicated terminalBackground field, update this helper
// and all other terminal files.
export const paletteFrom = (tenantConfig) => {
  const c = tenantConfig?.colors || {};
  return {
    bg: c.secondary || '#050810',
    card: '#0f1420',
    accent: c.primary || '#ff6b00',
    border: '#1e2a3f',
    borderBright: '#2a3d5c',
    text: '#ffffff',
    textDim: 'rgba(255,255,255,0.6)',
    inputBorder: 'rgba(255,255,255,0.1)',
    error: '#ff6b6b',
    errorBg: 'rgba(255,107,107,0.08)',
  };
};

// useIsMobile — live-updating viewport-width check.
//
// Uses requestAnimationFrame for natural render-tick flushing so the UI
// reacts within one frame instead of a fixed timeout. Trailing semantics:
// only the latest width wins if multiple resize events fire in the same
// frame, so a desktop → mobile → desktop flurry resolves correctly.
//
// SSR-safe: the useState initializer checks for window existence before
// reading innerWidth, so no flash of wrong content on hydration.
export const useIsMobile = (threshold = MOBILE_THRESHOLD) => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < threshold
  );

  useEffect(() => {
    let rafId = 0;
    const handleResize = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setIsMobile(window.innerWidth < threshold);
      });
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(rafId);
    };
  }, [threshold]);

  return isMobile;
};

// Full-screen spinner over the terminal dark bg, shown while tenantConfig is
// loading or the caller explicitly signals a loading state.
export const FullscreenSpinner = ({ palette }) => (
  <Box sx={{
    height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    bgcolor: palette?.bg || '#050810',
  }}>
    <CircularProgress size={28} sx={{ color: palette?.accent || '#22c55e' }} />
  </Box>
);

// MobileBlockView — rendered inside the shell's card when the viewport is
// below MOBILE_THRESHOLD. Same card chrome as every other terminal auth
// surface (logo is already rendered by the shell above this component).
// "Go to mobile app" targets {publicUrl}/app when the tenant exposes a
// publicUrl, otherwise navigates internally to /app (which redirects to
// /app/dashboard). /app/home is intentionally NOT used — that route does
// not exist and would 404.
const MobileBlockView = ({ palette, publicUrl }) => {
  const { t } = useTranslation();
  const hasExternal = Boolean(publicUrl);
  const buttonProps = hasExternal
    ? { component: 'a', href: `${publicUrl}/app` }
    : { component: RouterLink, to: '/app' };

  return (
    <Stack spacing={2} alignItems="center">
      <Typography sx={{
        fontFamily: MONO, fontWeight: 500, fontSize: 20,
        color: palette.text, letterSpacing: '-0.01em', textAlign: 'center',
      }}>
        {t('terminal.auth.mobileBlock.title')}
      </Typography>
      <Typography sx={{
        fontFamily: MONO, fontSize: 13, color: palette.textDim,
        textAlign: 'center', lineHeight: 1.55,
      }}>
        {t('terminal.auth.mobileBlock.description')}
      </Typography>
      <Button
        {...buttonProps}
        fullWidth
        sx={{
          mt: 2,
          bgcolor: palette.accent,
          color: palette.bg,
          fontFamily: MONO, fontSize: 12, fontWeight: 700,
          letterSpacing: '0.1em', textTransform: 'uppercase',
          borderRadius: 0, py: 1.5,
          '&:hover': { bgcolor: '#ffffff', boxShadow: `0 0 20px ${palette.accent}33` },
        }}
      >
        {t('terminal.auth.mobileBlock.goToMobileApp')}
      </Button>
    </Stack>
  );
};

// Branded auth-shell shared by /terminal/login, /terminal/forgot-password,
// /terminal/reset-password, and the mobile-block view on /terminal itself.
// Owns the outer dark bg, the centered 420px card, and the tenant-branded
// header (logo with image→text fallback + caller-supplied subtitle). Form
// content is passed via children.
//
// When showMobileBlock is true (default) and the viewport is below
// MOBILE_THRESHOLD, the shell renders MobileBlockView instead of children.
// Children are not mounted in that case — important for avoiding password-
// manager confusion from login forms that are present but invisible.
//
// Props:
//   - subtitle: React node rendered under the logo (typically the page
//     title such as "{appName} Terminal" or "Two-factor authentication")
//   - topSlot: optional React node rendered above the logo (e.g. a Back
//     button in the 2FA step). Hidden in mobile-blocked state.
//   - dimmed: apply pointer-events: none + opacity 0.8 to the card while a
//     form submit is in flight
//   - loading: force FullscreenSpinner regardless of tenantConfig state,
//     used by callers that also want to gate on useUserData isLoading
//   - showMobileBlock: when true (default), viewports under 1024px see a
//     MobileBlockView instead of children. Callers that explicitly want
//     to render on any viewport can pass false.
//   - children: form content (error banner, inputs, buttons, etc.)
const TerminalAuthShell = ({
  subtitle = null,
  topSlot = null,
  dimmed = false,
  loading = false,
  showMobileBlock = true,
  children,
}) => {
  const { tenantConfig } = useTenantTheme();
  const [logoFailed, setLogoFailed] = useState(false);
  const palette = useMemo(() => paletteFrom(tenantConfig), [tenantConfig]);
  const isMobile = useIsMobile();

  if (loading || !tenantConfig) {
    return <FullscreenSpinner palette={palette} />;
  }

  const isBlocked = showMobileBlock && isMobile;
  const appName = tenantConfig.name || 'FlockTrade';
  const logoSrc = tenantConfig.logo;
  const showLogoImage = logoSrc && !logoFailed;
  const publicUrl = tenantConfig?.settings?.publicUrl || null;

  return (
    <Box sx={{
      minHeight: '100vh', width: '100%',
      bgcolor: palette.bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      p: 2,
    }}>
      <Box sx={{
        width: '100%', maxWidth: 420,
        bgcolor: palette.card,
        border: `1px solid ${palette.border}`,
        borderRadius: '8px',
        p: { xs: 4, sm: 6 },
        pointerEvents: dimmed ? 'none' : 'auto',
        opacity: dimmed ? 0.8 : 1,
        transition: 'opacity 0.15s',
      }}>
        {!isBlocked && topSlot && <Box sx={{ mb: 2 }}>{topSlot}</Box>}
        <Stack spacing={1.25} alignItems="center" sx={{ mb: isBlocked ? 3 : 4 }}>
          {showLogoImage ? (
            <Box
              component="img"
              src={logoSrc}
              alt={appName}
              onError={() => setLogoFailed(true)}
              sx={{ height: 48, maxWidth: 220, objectFit: 'contain' }}
            />
          ) : (
            <Typography sx={{
              fontFamily: MONO, fontWeight: 500, fontSize: 32,
              color: palette.text, letterSpacing: '-0.02em',
            }}>
              {appName}
            </Typography>
          )}
          {!isBlocked && subtitle}
        </Stack>
        {isBlocked
          ? <MobileBlockView palette={palette} publicUrl={publicUrl} />
          : children
        }
      </Box>
    </Box>
  );
};

export default TerminalAuthShell;
